/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useAuth } from '@isocel/front-common';
import Modal from 'antd/lib/modal/Modal';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { usePackagingConfig } from '../../queries/package';
import { createPackagingFlow, downloadPackagingVoucher } from '../../queries/api/packaging-flow';
import { ShipmentSuccess } from '../shipment';
import '../shipment/ShipmentTrack.css';
import '../packingMovement/movement.less';
import './QrPackageTrack.less';
import '../shipment/Support.css';
import '../receptionStore/receptionView/ReceptionView.less';
import { PackagingFlowItem } from '../../queries/api/types';
import { useNotification } from '../notification/Notification';
import { printPDF } from '../../utils/utils';
import defaultImage from '../../assets/images/default.png';
import { PackageIcon } from '../../components/PackageIcon';
const QrPackageTrack: React.FC = () => {
    const {
        packagingFlowData,
        setPackageFlow,
        selectedParc,
        flowType,
        originalQuantities,
        updateComment,
        setUpdateComment,
    } = usePackagingFlow();
    const { data: packageConfig } = usePackagingConfig();
    const history = useHistory();
    const [comment, setComment] = useState<string>('');
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    useEffect(() => {
        setComment(updateComment || packagingFlowData[0]?.comment || '');
    }, []);

    const handlePrint = async () => {
        if (!selectedVoucherId) return;

        const response = await downloadPackagingVoucher(selectedVoucherId);
        await printPDF(
            new Blob([response.data], { type: 'application/pdf' }),
            () => {
                setIsModalVisible(false);
                setIsSuccess(true);
            },
            () => {
                showNotification('error', "Erreur lors de l'impression du bon d'emballage", '');
            },
            () => {
                setTimeout(() => {
                    setIsSuccess(true);
                }, 2000);
            }
        );
    };

    const handlePackageClick = (
        pkg: PackagingFlowItem,
        modelName: string,
        typeName: string,
        typeImage: string,
        categoryName: string
    ) => {
        history.push({
            pathname: '/qrPackageUpdate',
            state: {
                package: pkg,
                modelName: modelName,
                typeName: typeName,
                categoryName: categoryName,
                typeImage: typeImage,
            },
        });
        setUpdateComment(comment);
    };

    // Aggregate packages by packageId for display
    function aggregatePackagesByPackageId(packages: PackagingFlowItem[]) {
        const map = new Map<string, PackagingFlowItem>();
        packages.forEach((pkg) => {
            const packageId = typeof pkg.package === 'string' ? pkg.package : pkg.package.id;
            if (!map.has(packageId)) {
                map.set(packageId, { ...pkg });
            } else {
                const existing = map.get(packageId)!;
                existing.goodCount += pkg.goodCount;
                existing.badCount += pkg.badCount;
                existing.shippedQuantity += pkg.shippedQuantity;
            }
        });
        return Array.from(map.values());
    }

    const handleSave = async () => {
        const isSCAVoucher = packagingFlowData.some((flow) =>
            flow.packagingVouchers?.some((voucher) => voucher.voucherNumber.startsWith('SCA-'))
        );
        // Combine all packages from all flows and aggregate by packageId
        const allPackages = packagingFlowData.flatMap((flow) => flow.packages || []);
        const combinedPackages = aggregatePackagesByPackageId(allPackages);

        // Validate that at least one package has quantity > 0
        const totalQuantity = combinedPackages.reduce(
            (sum, pkg) => sum + (pkg.goodCount || 0) + (pkg.badCount || 0) + (pkg.shippedQuantity || 0),
            0
        );
        if (totalQuantity === 0) {
            showNotification(
                'error',
                'Validation',
                'Au moins un emballage avec une quantité supérieure à 0 est requis.'
            );
            return;
        }

        const req = {
            user: user?.id || '',
            flowType: flowType,
            sender: isSCAVoucher ? packagingFlowData[0].sender.id : selectedParc?.id || '',
            recipient: isSCAVoucher ? packagingFlowData[0].recipient : packagingFlowData[0].sender.id,
            packages: combinedPackages.map((pkg) => {
                const packageId = typeof pkg.package === 'string' ? pkg.package : pkg.package.id;
                const originalQuantity = originalQuantities[packageId] || 0;
                const updatedCount = pkg.goodCount + pkg.badCount;
                return {
                    ...pkg,
                    toBeReturned: Math.max(0, originalQuantity - updatedCount),
                };
            }),
            packagingVoucherUsed: packagingFlowData.flatMap((flow) =>
                (flow.packagingVouchers || []).map((voucher) => voucher)
            ),
            comment: comment,
            organization: user?.organization?.id,
        };

        setPackageFlow(req);
        try {
            const response = await createPackagingFlow(req);
            if (response) {
                const voucherId = response.packagingVouchers?.[0];

                if (voucherId) {
                    setSelectedVoucherId(voucherId as unknown as string);
                    setIsModalVisible(true);
                } else {
                    setIsSuccess(true);
                }
            }
        } catch (error) {
            showNotification('error', 'Erreur lors de la création de packagingFlow', '');
        }
    };

    const change = (value: string) => {
        setComment(value);
    };

    if (isSuccess) {
        return <ShipmentSuccess />;
    }

    const renderPackages = (packages: PackagingFlowItem[]) => {
        return packages.map((pkg) => {
            const matchedConfig = packageConfig?.packages.find((config) => config.id === pkg.package);
            const typeName = matchedConfig?.type.name || '';
            const modelName = matchedConfig?.model.name || '';
            const categoryName = matchedConfig?.category.name || '';
            const typeImage = matchedConfig?.type.image || defaultImage;
            if (matchedConfig) {
                return (
                    <div
                        key={typeof pkg.package === 'string' ? pkg.package : pkg.package.id}
                        className="tab-panel"
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePackageClick(pkg, modelName, typeName, typeImage, categoryName)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handlePackageClick(pkg, modelName, typeName, typeImage, categoryName);
                            }
                        }}
                    >
                        <div className="package-item">
                            <div className="session-box align-flex flex direction items-center">
                                <div className="flex direction">
                                    <div className="item-info pis items-center">
                                        <PackageIcon
                                            fileId={matchedConfig?.type.iconRemoteFile?.id}
                                            className="header-type-image"
                                            alt="package-type"
                                            defaultImage={typeImage}
                                        />
                                        <span className="bold">{typeName}</span>
                                        {typeName !== modelName && <span className=" bold py-16">-</span>}
                                        {typeName !== modelName && <span className="py-16">{modelName}</span>}
                                        <span className="tag tag-grey">
                                            {' '}
                                            {pkg.goodCount + pkg.badCount || 0} palettes
                                        </span>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <strong>{pkg.goodCount || 0}</strong> <span>Bon état</span> &nbsp;{' '}
                                    <strong>{pkg.badCount || 0}</strong> <span>Cassée</span>
                                    <span className="right-arrow">
                                        {' '}
                                        <RightOutlined />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
            return null;
        });
    };

    return (
        <>
            <div className="title mb-16">
                <LeftOutlined className="back-icon" onClick={() => history.goBack()} />
            </div>
            <div className="reception-view has-comment support-container bg-dash">
                <header className="header">
                    <div className="location">
                        <h3>Transporteur: {packagingFlowData[0]?.sender?.name}</h3>
                        <h3>
                            Bons utilisés:{' '}
                            {packagingFlowData
                                .flatMap((flow) =>
                                    (flow.packagingVouchers || []).map((voucher) => voucher.voucherNumber)
                                )
                                .join(', ')}
                        </h3>
                    </div>
                </header>
                <h3 className="support">Supports & Emballages</h3>
                {packagingFlowData.map((flow) => (
                    <div key={flow._id} className="flow-section">
                        {renderPackages(flow.packages)}
                    </div>
                ))}
            </div>
            <div className="bottom flex flex-col">
                <div className="comment-section">
                    <h4>Commentaire</h4>
                    <textarea
                        placeholder="Remarque sur un support"
                        value={comment}
                        onChange={(e) => change(e.target.value)}
                    ></textarea>
                </div>
                <footer>
                    <div className="action-buttons flex justify-center">
                        <Button type="primary" onClick={handleSave} style={{ marginTop: '20px' }}>
                            Valider
                        </Button>
                    </div>
                </footer>
            </div>
            {/* Modal for Printing */}
            <Modal
                title="Imprimer le nouveau bon d'emballages"
                visible={isModalVisible}
                closable={false}
                maskClosable={false}
                footer={[
                    <Button key="print" type="primary" onClick={handlePrint}>
                        Imprimer
                    </Button>,
                ]}
            >
                <p>
                    Le nombre d&apos;emballages rendu est inférieur à celui attendu. Veuillez imprimer un nouveau bon
                    avec la quantité restante à restituer.
                </p>
            </Modal>
        </>
    );
};

export default QrPackageTrack;
